import { sqliteTable, text, real, integer, index } from "drizzle-orm/sqlite-core";
import { journalEntryItems } from "./journal-entry-items";

// ============================================================================
// je_item_allocations — analytic distribution rows for a JE/invoice line
//
// EPIC #412 Phase 2A — supersedes the single (lineReferenceModule, doctype, id)
// triple on journal_entry_items / invoice_items / gl_entries with a per-line
// child table that supports:
//
//   * split percentages — one line allocated 60% to vehicle A and 40% to
//     vehicle B at posting time without forcing the user to physically split
//     the JE into two lines.
//   * multi-tag — one line tagged with both a fleet vehicle AND a CRM
//     campaign AND a customer so several analytic dimensions roll up
//     independently.
//   * standalone dimension values — Phase 3 will populate `dimension_type_id`
//     + `dimension_value_id` for tags that don't correspond to an existing
//     module's doctype (campaigns, regions, customer tiers).
//
// Backwards compatibility: when no allocation rows exist for a JE item the
// posting / reporting code must fall back to the legacy lineRef columns
// installed in Phase 1A. The Phase 2B backfill copies every legacy lineRef
// into a single 100% allocation; once it has run the legacy columns can be
// dropped (deferred to a later phase).
// ============================================================================

export const jeItemAllocations = sqliteTable("accounting_je_item_allocations", {
  id: text("id").primaryKey(), // ULID
  jeItemId: text("je_item_id").notNull().references(() => journalEntryItems.id),

  // Doctype-based dimension (Phase 1 pattern, polymorphic FK)
  referenceModule: text("reference_module"),
  referenceDoctype: text("reference_doctype"),
  referenceId: text("reference_id"),

  // Standalone dimension (Phase 3) — admin-defined dimension types whose
  // values aren't backed by a doctype (campaign / region / tier).
  dimensionTypeId: text("dimension_type_id"),
  dimensionValueId: text("dimension_value_id"),

  // Split values. percentage is 0–100 (NULL means single 100% allocation —
  // i.e. the row carries the entire amount and percentage is implicit).
  // amount is the distributed amount in base currency: jeItem.(debit+credit)
  // * percentage / 100 for splits, or jeItem.(debit+credit) for a single.
  percentage: real("percentage"),
  amount: real("amount").notNull().default(0),

  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: text("created_at").notNull(),
}, (table) => ({
  // Join from JE item to its allocations.
  jeiAllocItemIdx: index("jei_alloc_item_idx").on(table.jeItemId),
  // Group-by-reference query path (per-vehicle / per-rental / per-asset P&L).
  jeiAllocRefIdx: index("jei_alloc_ref_idx").on(table.referenceModule, table.referenceId),
  // Phase 3 dimension lookups.
  jeiAllocDimIdx: index("jei_alloc_dim_idx").on(table.dimensionTypeId, table.dimensionValueId),
}));

export type JeItemAllocation = typeof jeItemAllocations.$inferSelect;
export type NewJeItemAllocation = typeof jeItemAllocations.$inferInsert;
